export type StudyTelemetryEventType =
  | 'SESSION_STARTED'
  | 'PAGE_OPENED'
  | 'PAGE_CHANGED'
  | 'PROBLEM_FIRST_INTERACTION'
  | 'STROKE_COMMITTED'
  | 'ERASE_USED'
  | 'UNDO_USED'
  | 'SESSION_PAUSED'
  | 'SESSION_RESUMED'
  | 'FINISH_REQUESTED'
  | 'SESSION_SUBMITTED'
  | 'GRADE_RECEIVED'
  | 'CORRECTION_REQUIRED'
  | 'CORRECTION_SUBMITTED'
  | 'SESSION_COMPLETED'
  | 'SESSION_FINISHED_LOCAL';

export type StudyTelemetryEvent = {
  eventId: string;
  sessionId: string;
  type: StudyTelemetryEventType;
  atMs: number;
  pageId?: string;
  problemId?: string;
  metadata?: Record<string, string | number | boolean>;
};

export type StudyTelemetryCollector = {
  record: (event: Omit<StudyTelemetryEvent, 'eventId'>) => StudyTelemetryEvent | null;
  events: () => readonly StudyTelemetryEvent[];
};

/**
 * Low-volume pedagogical telemetry only. Raw pointer moves and raw ink points
 * are deliberately excluded; those remain solely inside the ink document.
 */
export function createStudyTelemetryCollector(): StudyTelemetryCollector {
  const recorded: StudyTelemetryEvent[] = [];
  const firstInteraction = new Set<string>();

  function record(event: Omit<StudyTelemetryEvent, 'eventId'>): StudyTelemetryEvent | null {
    if (event.type === 'PROBLEM_FIRST_INTERACTION') {
      if (!event.problemId) throw new Error('PROBLEM_FIRST_INTERACTION requires problemId.');
      const key = `${event.sessionId}:${event.problemId}`;
      if (firstInteraction.has(key)) return null;
      firstInteraction.add(key);
    }

    const stored: StudyTelemetryEvent = {
      ...event,
      eventId: `${event.sessionId}-${recorded.length + 1}-${event.type}`,
    };
    recorded.push(stored);
    return stored;
  }

  return {
    record,
    events: () => recorded,
  };
}
